import { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Pencil, Power } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import {
  PageError,
  PageLoading,
  PageNotFound,
} from '@/components/ui/PageAsyncState'
import { SupplierStatusBadge } from '@/features/suppliers/SupplierStatusBadge'
import type { PriceRule } from '@/services/quotes'
import {
  supplierService,
  type Supplier,
} from '@/services/suppliers'
import { QuoteSheetPanel } from '@/features/suppliers/QuoteSheetPanel'
import {
  supplierFormatService,
  filterFormatsBySupplierCategories,
  isWoodFormat,
  type SupplierFormatDef,
} from '@/services/suppliers/supplierFormatService'
import { formatDateFull } from '@/lib/dateFormat'
import { useCan } from '@/features/auth/AuthContext'
import { listSupplierNegotiatedPrices, type NegotiatedPriceDto, money } from '@/services/procurement'
import { FileExplorer } from '@/components/files/FileExplorer'
import { supplierWorkspaceService } from '@/services/suppliers/supplierWorkspaceService'

export { WOOD_FORMAT_IDS, isWoodFormat } from '@/services/suppliers/supplierFormatService'

type TabId = 'info' | 'quotes' | 'negotiated'

const TABS: { id: TabId; label: string }[] = [
  { id: 'quotes', label: 'Báo giá' },
  { id: 'info', label: 'Thông tin NCC' },
  { id: 'negotiated', label: 'Giá thương lượng' },
]

const DEFAULT_SUPPLIER_TAB: TabId = 'quotes'

// Đọc ?tab= từ đường dẫn; giá trị lạ thì về tab mặc định "Báo giá".
export function tabFromParam(value: string | null): TabId {
  return TABS.some((t) => t.id === value) ? (value as TabId) : DEFAULT_SUPPLIER_TAB
}

export interface SupplierQuoteItemRow extends Record<string, unknown> {
  id: string
  name: string
  t: number | null
  w: number | null
  l: number | null
  uom: string
  price: number | null
  notes: string
  rule_id?: string
  item_id?: string | null
  normalized_unit_price?: number | null
  normalized_price_uom?: string | null
  normalized_price_basis_qty?: number | null
}

/** Map API price rules → editable quote table rows (used by load + round-trip tests). */
export function priceRulesToQuoteRows(
  rules: PriceRule[],
  format: SupplierFormatDef | undefined,
  quoteType: string,
): SupplierQuoteItemRow[] {
  const isWood = isWoodFormat(quoteType)
  return rules.map((r, i) => {
    const spec = (r.spec_attributes || {}) as Record<string, unknown>
    const orig = (r.original_values || {}) as Record<string, unknown>

    if (isWood) {
      return {
        id: r.id || `rule-${i + 1}`,
        rule_id: r.id,
        item_id: r.item_id || null,
        name: r.supplier_item_name,
        t: (spec.t ?? spec.thickness ?? null) as number | null,
        w: (spec.w ?? spec.width ?? null) as number | null,
        l: (spec.l ?? spec.length ?? null) as number | null,
        uom: r.uom || '',
        price: r.unit_price,
        normalized_unit_price: r.normalized_unit_price ?? null,
        normalized_price_uom: r.normalized_price_uom ?? null,
        normalized_price_basis_qty: r.normalized_price_basis_qty ?? null,
        notes: (orig.notes ?? '') as string,
      }
    }

    const notesVal = (orig.notes ?? spec.notes ?? '') as string
    const baseRow: SupplierQuoteItemRow = {
      id: r.id || `rule-${i + 1}`,
      rule_id: r.id,
      item_id: r.item_id || null,
      name: r.supplier_item_name || (spec.name as string) || (spec.kind as string) || '',
      t: null,
      w: null,
      l: null,
      uom: r.uom || '',
      price: r.unit_price,
      normalized_unit_price: r.normalized_unit_price ?? null,
      normalized_price_uom: r.normalized_price_uom ?? null,
      normalized_price_basis_qty: r.normalized_price_basis_qty ?? null,
      notes: notesVal,
    }

    if (format) {
      for (const col of format.columns) {
        if (col.key === 'stt' || col.key === 'id') continue
        if (col.role === 'identity' || col.key === 'name') {
          baseRow.name = r.supplier_item_name || (spec.name as string) || ''
          baseRow[col.key] = spec[col.canonical_key || col.key] ?? r.supplier_item_name ?? ''
        } else if (col.key === 'kind') {
          baseRow.kind = (spec.kind as string) || r.supplier_item_name || ''
        } else if (col.canonical_key === 'price' || col.key === 'price') {
          baseRow.price = r.unit_price
          baseRow[col.key] = spec[col.canonical_key || col.key] ?? r.unit_price
        } else if (col.canonical_key === 'uom' || col.key === 'uom') {
          baseRow.uom = r.uom || ''
          baseRow[col.key] = spec[col.canonical_key || col.key] ?? r.uom ?? ''
        } else if (col.key === 'notes') {
          baseRow.notes = notesVal
        } else {
          if (isColorFormatColumn(col)) {
            const color = spec.color ?? spec[col.canonical_key || col.key] ?? spec[col.key] ?? spec.brand ?? orig.color ?? orig[col.canonical_key || col.key] ?? orig[col.key] ?? orig.brand ?? ''
            baseRow.color = color
            baseRow[col.key] = color
          } else {
            baseRow[col.key] = spec[col.canonical_key || col.key] ?? spec[col.key] ?? orig[col.key] ?? ''
          }
        }
      }
    } else {
      for (const [k, v] of Object.entries(spec)) {
        if (!(k in baseRow)) {
          baseRow[k] = v
        }
      }
    }

    if (spec.kind && !baseRow.kind) {
      baseRow.kind = spec.kind
    }

    return baseRow
  })
}

function isColorFormatColumn(column: SupplierFormatDef['columns'][number]): boolean {
  return column.key === 'color' || column.key === 'brand' || column.canonical_key === 'color' || /màu|colour/i.test(column.label)
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 border-b border-slate-100 py-2 text-sm last:border-0">
      <dt className="text-[var(--color-text-muted)]">{label}</dt>
      <dd className="font-medium text-[var(--color-text)] break-words">
        {value || '—'}
      </dd>
    </div>
  )
}

function InfoBlock({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
      <div className="border-b border-[var(--color-border)] bg-slate-50 px-3 py-2 text-xs font-semibold tracking-wide text-slate-600 uppercase">
        {title}
      </div>
      <dl className="px-3 py-1 sm:px-4">{children}</dl>
    </section>
  )
}

/** Info-tab label: all matching format names joined by ", ", or "—" if none. */
export function formatSupplierGoodsTypesLabel(
  formats: { name: string }[],
): string {
  if (formats.length === 0) return '—'
  return formats.map((f) => f.name).join(', ')
}

export function isQuoteRowsDirty(
  currentRows: SupplierQuoteItemRow[],
  baselineRows: SupplierQuoteItemRow[] | null,
  isEditMode: boolean,
): boolean {
  if (!isEditMode || !baselineRows) return false
  return JSON.stringify(currentRows) !== JSON.stringify(baselineRows)
}

export function SupplierDetailPage() {
  const canWriteNegotiated = useCan('po.write')
  const canApproveNegotiated = useCan('po.approve')
  const canSeeNegotiated = canWriteNegotiated || canApproveNegotiated
  const { supplierId = '' } = useParams()
  const navigate = useNavigate()
  const [supplier, setSupplier] = useState<Supplier | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<TabId>(() => tabFromParam(searchParams.get('tab')))
  const [negotiated, setNegotiated] = useState<NegotiatedPriceDto[]>([])
  const [negotiatedStatus, setNegotiatedStatus] = useState('ALL')
  const [negotiatedSearch, setNegotiatedSearch] = useState('')
  const [negotiatedError, setNegotiatedError] = useState('')
  useEffect(()=>{
    if(tab!=='negotiated'||!supplierId||!canSeeNegotiated)return
    void listSupplierNegotiatedPrices(supplierId).then(rows=>{setNegotiated(rows);setNegotiatedError('')}).catch(error=>setNegotiatedError(error instanceof Error?error.message:'Không tải được giá thương lượng'))
  },[tab,supplierId,canSeeNegotiated])
  const [busy, setBusy] = useState(false)
  const [formats, setFormats] = useState<SupplierFormatDef[]>(() =>
    supplierFormatService.getAll(),
  )


  useEffect(() => {
    if (supplier) {
      void supplierFormatService.fetchFormats(supplier.id).then((fresh) => {
        setFormats([...fresh])
      })
    }
  }, [supplier])

  useEffect(() => {
    if (tab === 'quotes') {
      setFormats(supplierFormatService.getAll())
    }
  }, [tab])

  const matchingFormats = useMemo(() => {
    if (!supplier) return [] as SupplierFormatDef[]
    return filterFormatsBySupplierCategories(formats, supplier.category_ids)
  }, [formats, supplier])

  const handleTabChange = useCallback(
    (nextTab: TabId) => {
      if (nextTab === tab) return
      setTab(nextTab)
    },
    [tab],
  )

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const s = await supplierService.getById(supplierId)
      setSupplier(s)
      if (!s) setError('Không tìm thấy nhà cung cấp.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi tải NCC')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId])

  async function toggleStatus() {
    if (!supplier) return
    const next = supplier.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    const msg =
      next === 'INACTIVE'
        ? `Ngừng hoạt động "${supplier.code}"? NCC vẫn xem được lịch sử, không dùng cho giao dịch mua mới.`
        : `Kích hoạt lại "${supplier.code}"?`
    if (!window.confirm(msg)) return
    setBusy(true)
    setError(null)
    try {
      const updated = await supplierService.setStatus(supplier.id, next)
      setSupplier(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không đổi được trạng thái')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <PageLoading />
  }

  if (!supplier) {
    return (
      <PageNotFound
        title="Không tìm thấy NCC"
        description={error ?? 'supplier id không tồn tại trong mock.'}
        action={
          <Button variant="secondary" onClick={() => navigate('/purchasing/suppliers')}>
            Quay lại danh sách
          </Button>
        }
      />
    )
  }

  const categoryLabel = supplierService.categoryLabels(supplier.category_ids)
  const paymentLabel =
    supplierService.getPaymentTerm(supplier.payment_term_id)?.name ?? '—'

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Nhà cung cấp', to: '/purchasing/suppliers' },
          { label: supplier.code },
        ]}
      />
      <PageHeader
        title={`${supplier.code} – ${supplier.name}`}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span>{categoryLabel}</span>
            <SupplierStatusBadge status={supplier.status} />
          </span>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => navigate(`/purchasing/po/new?supplierId=${supplier.id}`)}
            >
              Tạo đơn hàng PO
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate(`/purchasing/suppliers/${supplier.id}/edit`)}
            >
              <Pencil className="h-4 w-4" />
              Sửa
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => void toggleStatus()}>
              <Power className="h-4 w-4" />
              {supplier.status === 'ACTIVE' ? 'Ngừng hoạt động' : 'Kích hoạt'}
            </Button>
          </div>
        }
      />

      {error ? <PageError message={error} /> : null}

      <div className="mb-3 flex flex-wrap gap-1 border-b border-[var(--color-border)]">
        {TABS.filter(t=>t.id!=='negotiated'||canSeeNegotiated).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => handleTabChange(t.id)}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'border-b-2 border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'info' ? (
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <InfoBlock title="Thông tin cơ bản">
            <InfoRow label="Mã NCC" value={supplier.code} />
            <InfoRow label="Tên NCC" value={supplier.name} />
            <InfoRow
              label="Nhóm hàng"
              value={
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-slate-800">
                    {formatSupplierGoodsTypesLabel(matchingFormats)}
                  </span>
                </div>
              }
            />
            <InfoRow
              label="Trạng thái"
              value={<SupplierStatusBadge status={supplier.status} />}
            />
          </InfoBlock>

          <InfoBlock title="Liên hệ">
            <InfoRow label="Địa chỉ" value={supplier.address} />
            <InfoRow label="Người liên hệ" value={supplier.contact_person} />
            <InfoRow label="Điện thoại" value={supplier.phone} />
            <InfoRow label="Email" value={supplier.email} />
          </InfoBlock>

          <InfoBlock title="Thuế & mua hàng">
            <InfoRow label="Mã số thuế" value={supplier.tax_code} />
            <InfoRow
              label="VAT mặc định"
              value={
                <span className="tabular-nums">
                  {supplier.default_vat_rate.toFixed(2)}%
                </span>
              }
            />
            <InfoRow label="Điều khoản" value={paymentLabel} />
            <InfoRow
              label="Lead Time mặc định"
              value={
                <span className="tabular-nums">{supplier.lead_time_days} ngày</span>
              }
            />
          </InfoBlock>

          <InfoBlock title="Khác">
            <InfoRow label="Ghi chú" value={supplier.note} />
          </InfoBlock>
        </div>
      ) : null}

      {tab === 'quotes' ? (
        <div className="space-y-4">
          <QuoteSheetPanel supplierId={supplierId} />
          {/* Workspace Hồ sơ & Tài liệu Báo giá NCC (Đa file, cây thư mục trên server) */}
          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                  <span>📁</span> Hồ sơ & Tài liệu Báo giá NCC
                </h3>
                <p className="text-xs text-slate-500">
                  Quản lý các file báo giá, phụ lục, hợp đồng scan, tài liệu kỹ thuật theo cây thư mục.
                </p>
              </div>
            </div>
            <FileExplorer
              productCode={supplier.id}
              folderSlug="quotes"
              rootLabel="Hồ sơ báo giá NCC"
              service={supplierWorkspaceService}
            />
          </div>
        </div>
      ) : null}

      {tab === 'negotiated' && canSeeNegotiated ? <section className="rounded-lg border bg-white p-4">
        <h2 className="text-lg font-semibold">Giá thương lượng của nhà cung cấp</h2>
        <p className="mt-1 text-sm text-slate-600">Giá chỉ áp dụng cho PO được ghi trên từng dòng. Muốn dùng cho các lần mua sau, nhập thành báo giá và kích hoạt theo quy trình báo giá.</p>
        {negotiatedError&&<p role="alert" className="mt-2 text-red-700">{negotiatedError}</p>}
        <div className="my-3 flex flex-wrap gap-2"><input aria-label="Tìm giá thương lượng" className="rounded border p-2" placeholder="Tìm PO hoặc vật tư" value={negotiatedSearch} onChange={e=>setNegotiatedSearch(e.target.value)}/><select aria-label="Lọc trạng thái giá thương lượng" className="rounded border p-2" value={negotiatedStatus} onChange={e=>setNegotiatedStatus(e.target.value)}><option value="ALL">Mọi trạng thái</option><option value="DRAFT">Nháp</option><option value="CONFIRMED">Đã xác nhận</option><option value="VOID">Đã hủy</option></select></div>
        <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead><tr>{['STT','PO','Vật tư','Giá','Giá tham chiếu','Hiệu lực','Trạng thái','Lý do'].map(label=><th key={label} className="border p-2 text-left">{label}</th>)}</tr></thead><tbody>{negotiated.filter(row=>(negotiatedStatus==='ALL'||row.status===negotiatedStatus)&&`${row.po_code??''} ${row.material_code??''} ${row.material_name??''}`.toLowerCase().includes(negotiatedSearch.toLowerCase())).map((row,i)=><tr key={row.id}><td className="border p-2">{i+1}</td><td className="border p-2"><button type="button" className="text-blue-700 underline" onClick={()=>navigate(`/purchasing/po/${row.po_id}`)}>{row.po_code??row.po_id}</button></td><td className="border p-2">{row.material_code} · {row.material_name}</td><td className="border p-2">{money(Number(row.unit_price))} / {Number(row.price_basis_qty)} {row.price_uom}</td><td className="border p-2">{row.reference_price==null?'—':money(Number(row.reference_price))}</td><td className="border p-2">{formatDateFull(row.valid_from)}</td><td className="border p-2">{row.status}</td><td className="border p-2">{row.reason}</td></tr>)}</tbody></table></div>
      </section> : null}

    </div>
  )
}
